<template>
  <div v-if="!loading" class="content-top-bottom" :class="{ 'list-of-pills': isSuggestionsScreenDisplayed }">
    <v-subheader :goBackButtonTo="{ name: 'myPills' }">
      <template slot="title">
        <span class="bold">Check data</span>
      </template>
      <v-button primary @click="saveAll" id="save-check-data-button" class="" :loading="saving">
        <i class="icon-save-all"></i>
        Save all
      </v-button>
    </v-subheader>
    <div class="container">
      <v-pagination
        v-model="tabIndex"
        :items="checkItems.length"
        class="check-data-pagination"
        @click="showPaginationPopup"
      >
        <span class="icon-expand" @click="showPaginationPopup"></span>
        <div v-if="paginationExpanded" class="list-item-select" v-click-outside="() => (paginationExpanded = false)">
          <div class="list-item-select__inner">
            <a
              v-for="(item, index) in checkItems"
              :key="item.listItemId"
              class="d-block list-item-select__item"
              @click.prevent="openItem(index)"
            >
              <span class="dot" :class="item.finalData.isPillVerified ? 'bg-green' : 'bg-dark-grey'"></span>
              {{ item.medicineName }}
            </a>
          </div>
        </div>
      </v-pagination>
    </div>
    <div class="check-data-group__top">
      <div class="container">
        <div class="check-data-group mt-15">
          <div class="check-data-group__title check-data-group__title--main">
            <span class="dot" :class="currentItem.finalData.isPillVerified ? 'bg-green' : 'bg-dark-grey'"></span>
            {{
              currentItem.finalData.isPillVerified
                ? $t("page.pill-shape-form.incorrect-values-corrected")
                : $t(currentItem.headerTextTranslationKey)
            }}
          </div>
          <div class="check-data-group__item">
            <div class="check-data-group__desc">
              <div class="check-data-group__col1">
                <label class="label-form">{{ currentMedKeyTypeName }} Number</label>
                <v-input
                  id="pzn-number-input"
                  :class="{
                    'text-red': hasNoChosenSuggestionOnSuggestionsScreen,
                    'text-primary': hasChosenSuggestion || isMismatchEdited,
                  }"
                  :value="pznValue"
                  disabled
                />
              </div>

              <div class="check-data-group__col2" v-if="isSuggestionsScreenDisplayed && getShape(currentItem)">
                <i class="icon">
                  <img :src="getIcon(getShape(currentItem).type)" />
                </i>
                <label class="label-form">Pill shape</label>
                <v-input :value="getShape(currentItem).type" disabled />
              </div>

              <div class="check-data-group__col3">
                <label class="label-form">Medicine Name</label>
                <v-input
                  id="pzn-medicine-name-input"
                  :class="{
                    'text-red': hasNoChosenSuggestionOnSuggestionsScreen,
                    'text-primary': hasChosenSuggestion,
                  }"
                  v-model="currentItem.finalData.medicineName"
                  :disabled="isMedicineNameAndManufacturerDisabled"
                />
              </div>

              <div class="check-data-group__col5">
                <label class="label-form">Manufacturer</label>
                <v-search-select
                  v-model="currentItem.finalData.manufacturer"
                  :disabled="isMedicineNameAndManufacturerDisabled"
                  :search-phrase="currentItem.finalData.manufacturer"
                  :options="manufacturers"
                />
              </div>

              <div v-if="!isSuggestionsScreenDisplayed" class="check-data-group__col4">
                <label>
                  <v-checkbox
                    type="checkbox"
                    v-model="currentItem.finalData.uvSensibility"
                    :disabled="currentItem.matchDbData && currentItem.matchDbData.uvSensibility"
                  />
                  <span>UV Sensibility</span>
                </label>
              </div>
            </div>
            <div v-if="isSuggestionsScreenDisplayed && getShape(currentItem)" class="group-size">
              <div class="group-size__item" :key="key" v-for="(value, key) in getShape(currentItem).dimensions">
                <label class="label-form">
                  {{ mapDimensionToLabel(key) }}
                </label>
                <v-input :value="value" disabled />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="check-data-group__bottom">
      <div class="container">
        <div v-if="isSuggestionsScreenDisplayed && hasSuggestionsFetched" class="check-data-group mt-15">
          <div class="check-data-group__title">Did you mean this?</div>
          <div class="check-data-group--overflow">
            <div
              @click="setSuggestion(suggestion)"
              class="check-data-group__item check-data-group__item--suggestion"
              :class="{
                active: currentItem.finalData.medId === suggestion.medId,
                no_shape: !suggestion.shape,
              }"
              v-for="suggestion in availableSuggestions[tabIndex]"
              :key="suggestion.medId"
            >
              <div class="check-data-group__desc">
                <div class="check-data-group__col1">
                  <label class="label-form">{{ currentMedKeyTypeName }} Number</label>
                  <div class="numbers-input">{{ suggestion.medKeys.join(", ") }}</div>
                  <!-- <v-input :value="suggestion.medKeys.join(', ')" disabled /> -->
                </div>
                <div v-if="suggestion.shape" class="check-data-group__col2">
                  <i class="icon">
                    <img :src="getIcon(suggestion.shape.type)" />
                  </i>
                  <label class="label-form">Pill shape</label>
                  <v-input :value="suggestion.shape.type" disabled />
                </div>

                <div class="check-data-group__col3">
                  <label class="label-form">Medicine Name</label>
                  <v-input :value="suggestion.medicineName" disabled />
                </div>

                <div class="check-data-group__col5">
                  <label class="label-form">Manufacturer</label>
                  <v-input :value="suggestion.manufacturer" disabled />
                </div>
              </div>
              <div v-if="suggestion.shape && suggestion.shape.dimensions" class="group-size">
                <div class="group-size__item" :key="key" v-for="(value, key) in suggestion.shape.dimensions">
                  <label class="label-form">
                    {{ mapDimensionToLabel(key) }}
                  </label>
                  <v-input :value="value" disabled />
                </div>
              </div>
            </div>
          </div>
          <a id="none-of-these-indicator" href="#" class="mt-30 link-center" @click.prevent="onNoneOfTheseClicked">
            None of these
          </a>
        </div>
        <v-pill-shape-form
          v-if="!isSuggestionsScreenDisplayed"
          :key="currentItem.listItemId"
          :class="{ 'mb-85': !isNewPillScreenOnSeveralOptionsViewDisplayed }"
          :convex="currentItem.finalData.shape && currentItem.finalData.shape.convex"
          :pill="mapShape(currentItem.finalData.shape)"
          @update="onPillUpdated"
          :freeze-shape="hasCheckDimensionsStatus"
          :match-pill="matchPill"
          :shape-dismatch="checkDimensionsShapeDismatch"
          :database-shape-name="currentItem.matchDbData && currentItem.matchDbData.shape.type.toLowerCase()"
          :approved-errors="approvedErrors[tabIndex]"
          :approved-mismatches="approvedMismatches[tabIndex]"
          match-mode
          @apply="applyMismatch"
          @ignore="ignoreMismatch"
        />

        <a
          v-if="isNewPillScreenOnSeveralOptionsViewDisplayed"
          id="back-to-options-indicator"
          class="link-center"
          href="#"
          @click.prevent="onReturnToOptionsClicked"
        >
          Back to options
        </a>
      </div>

      <v-error-popup v-if="saveErrorMessage" :description="saveErrorMessage" @close="saveErrorMessage = null" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { VInput, VCheckbox, VSearchSelect, VButton } from "shared";
import VPagination from "@/components/v-pagination";
import VSubheader from "@/components/v-subheader";
import VPillShapeForm from "@/components/pills/v-pill-shape-form";
import VErrorPopup from "@/components/v-error-popup";
import * as ManufacturersDAO from "@/dao/manufacturers-dao";
import * as WorkListsDAO from "@/dao/worklists-dao";
import * as ShapeUtils from "@/utils/ShapeUtils";
import * as ObjectUtils from "@/utils/ObjectUtils";

export default {
  name: "PCheckData",
  components: {
    VPagination,
    VCheckbox,
    VInput,
    VButton,
    VSubheader,
    VSearchSelect,
    VPillShapeForm,
    VErrorPopup,
  },
  data() {
    return {
      loading: true,
      saving: false,
      tabIndex: 0,
      checkItems: null,
      availableSuggestions: {},
      approvedErrors: [],
      approvedMismatches: [],
      manufacturers: [],
      saveErrorMessage: null,
      paginationExpanded: false,
    };
  },
  computed: {
    ...mapGetters("workLists", ["currentWorkListId"]),
    ...mapGetters("medKey", ["currentMedKeyTypeName"]),

    currentItem() {
      return this.checkItems[this.tabIndex];
    },
    pznValue() {
      if (this.isNewPillScreenOnSeveralOptionsViewDisplayed || this.hasChosenSuggestion) {
        return this.currentItem.medKey + " (New)";
      } else if (this.isMismatchEdited) {
        return this.currentItem.medKey + " (Edited)";
      } else {
        return this.currentItem.medKey;
      }
    },
    isMismatchEdited() {
      return (
        this.checkDimensionsShapeMatch &&
        this.currentItem.finalData.isPillVerified &&
        !ObjectUtils.jsonEqual(
          this.currentItem.matchDbData.shape.dimensions,
          this.currentItem.finalData.shape.dimensions
        )
      );
    },
    isNewPillScreenOnSeveralOptionsViewDisplayed() {
      return this.currentItem.finalData.severalOptionsView === "new_pill_screen";
    },
    isSuggestionsScreenDisplayed() {
      return this.currentItem.finalData.severalOptionsView === "suggestions_screen";
    },
    hasChosenSuggestion() {
      return !!this.currentItem.finalData.medId;
    },
    hasNoChosenSuggestionOnSuggestionsScreen() {
      return this.isSuggestionsScreenDisplayed && !this.hasChosenSuggestion;
    },
    isMedicineNameAndManufacturerDisabled() {
      return !this.hasNoMatchStatus && !this.isNewPillScreenOnSeveralOptionsViewDisplayed;
    },
    hasNoMatchStatus() {
      return this.currentItem.pillStatusId === "noMatch";
    },
    hasSeveralOptionsStatus() {
      return this.currentItem.pillStatusId === "severalOptions";
    },
    hasCheckDimensionsStatus() {
      return this.currentItem.pillStatusId === "checkDimensions";
    },
    hasSuggestionsFetched() {
      return !!this.availableSuggestions[this.tabIndex];
    },
    checkDimensionsShapeMatch() {
      const dbShape = this.currentItem.matchDbData && this.currentItem.matchDbData.shape.type;
      const userShape = this.currentItem.userImportData && this.currentItem.userImportData.shape.type;
      return this.hasCheckDimensionsStatus && dbShape && userShape && dbShape === userShape;
    },
    checkDimensionsShapeDismatch() {
      const dbShape = this.currentItem.matchDbData && this.currentItem.matchDbData.shape.type;
      const userShape = this.currentItem.userImportData && this.currentItem.userImportData.shape.type;
      return (this.hasCheckDimensionsStatus && dbShape && userShape && dbShape !== userShape) || false;
    },
    matchPill() {
      return this.hasCheckDimensionsStatus && this.currentItem.matchDbData
        ? this.mapShape(this.currentItem.matchDbData.shape)
        : null;
    },
  },
  methods: {
    async fetchSuggestions() {
      if (!this.hasSeveralOptionsStatus || this.hasSuggestionsFetched) return;

      this.$set(
        this.availableSuggestions,
        this.tabIndex,
        await WorkListsDAO.getCheckDataSuggestions(this.currentWorkListId, this.currentItem.listItemId)
      );
    },
    async saveAll() {
      this.saving = true;
      try {
        const data = this.checkItems.map((item) => {
          const { manufacturer, ...object } = item.finalData;
          const m = this.manufacturers.find((m) => m.name === manufacturer);
          if (m) {
            object.manufacturerId = m.key;
          } else {
            object.manufacturerName = manufacturer;
          }
          return object;
        });
        await WorkListsDAO.saveCheckData(this.currentWorkListId, data);
        await this.loadCheckData();
        this.saving = false;
      } catch (error) {
        this.saving = false;
        this.saveErrorMessage = this.$t(error.response.data.errorTranslationKey);
      }
    },
    setSuggestion(suggestion) {
      this.$set(this.currentItem.finalData, "medId", suggestion.medId);
      this.$set(this.currentItem.finalData, "medicineName", suggestion.medicineName);
      this.$set(this.currentItem.finalData, "manufacturer", suggestion.manufacturer);
      this.$set(this.currentItem.finalData, "shape", suggestion.shape);
      this.$set(this.currentItem.finalData, "isPillVerified", true);
    },
    onNoneOfTheseClicked() {
      this.$delete(this.currentItem.finalData, "medId");
      this.$set(this.currentItem.finalData, "severalOptionsView", "new_pill_screen");
      this.$set(this.currentItem.finalData, "medicineName", this.currentItem.medicineName);
      this.$set(this.currentItem.finalData, "manufacturer", this.currentItem.manufacturer);
      this.$set(this.currentItem.finalData, "uvSensibility", this.currentItem.userImportData.uvSensibility);
      this.$set(this.currentItem.finalData, "shape", this.currentItem.userImportData.shape);
    },
    onReturnToOptionsClicked() {
      this.$set(this.currentItem.finalData, "severalOptionsView", "suggestions_screen");
      this.$delete(this.currentItem.finalData, "uvSensibility");
      this.$delete(this.currentItem.finalData, "shape");
    },
    onPillUpdated({ pill, isPillVerified }) {
      this.$set(this.currentItem.finalData, "shape", ShapeUtils.mapPillToShape(pill));
      this.$set(this.currentItem.finalData, "isPillVerified", isPillVerified);
    },
    scrollTopList() {
      if (this.hasSeveralOptionsStatus) {
        const scroll = document.querySelector(".check-data-group--overflow");
        if (scroll) {
          const item = scroll.querySelector(".active");
          if (item) {
            item.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      }
    },
    applyMismatch() {
      this.currentItem.finalData.uvSensibility = ObjectUtils.getProperty(
        this.currentItem.matchDbData,
        "uvSensibility",
        this.currentItem.userImportData.uvSensibility
      );
      this.$delete(this.currentItem, "matchDbData");
    },
    ignoreMismatch() {
      this.$delete(this.currentItem, "matchDbData");
    },
    openItem(index) {
      this.tabIndex = index;
      this.paginationExpanded = false;
    },
    async loadCheckData() {
      this.checkItems = await WorkListsDAO.getCheckData(this.currentWorkListId);
      if (this.checkItems.length === 0) {
        this.$router.push({ name: "myPills" });
        return false;
      }
      this.tabIndex = 0;
      this.approvedErrors = [];
      this.approvedMismatches = [];
      for (let i = 0; i < this.checkItems.length; i++) {
        this.approvedErrors.push([]);
        this.approvedMismatches.push([]);

        const item = this.checkItems[i];
        if (
          item.matchDbData &&
          item.userImportData &&
          item.userImportData.shape.type === "oval" &&
          item.userImportData.shape.dimensions.diagonal &&
          !item.matchDbData.shape.dimensions.diagonal
        ) {
          item.matchDbData.shape.dimensions.diagonal = item.userImportData.shape.dimensions.diagonal;
        }
      }
      return true;
    },
    showPaginationPopup() {
      if (!this.paginationExpanded) {
        setTimeout(() => {
          this.paginationExpanded = true;
        });
      }
    },
    mapShape: ShapeUtils.mapShape,
    mapDimensionToLabel(dimension) {
      return {
        height: "Height h [mm]",
        width: "Width w1 [mm]",
        width2: "Width w2 [mm]",
        diameter: "Diameter d [mm]",
        thickness: "Thickness t1 [mm]",
        thickness2: "Thickness t2 [mm]",
        diagonal: "Diagonal c [mm]",
      }[dimension];
    },
    getShape(item) {
      return item.finalData.shape || item.userImportData.shape;
    },
    getIcon(tab) {
      if (!tab) {
        return;
      }
      return require("../assets/pill/" + tab.toLowerCase() + ".svg");
    },
  },
  async created() {
    if (!this.currentWorkListId) {
      this.$router.push({ name: "myPills" });
    }

    if (await this.loadCheckData()) {
      await this.fetchSuggestions();
      this.manufacturers = await ManufacturersDAO.getManufacturers();
      this.loading = false;
    }
  },
  watch: {
    tabIndex() {
      this.fetchSuggestions();
      setTimeout(() => {
        this.scrollTopList();
      }, 500);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/variables";
::-webkit-scrollbar {
  width: 7px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  border-radius: 6px;
  background: #d8d8d8;
}
::-webkit-scrollbar-thumb:hover {
  background: #555;
}
::v-deep .search-select--disabled .search-select__icon {
  display: none;
}
.numbers-input {
  width: 100%;
  background: #fbfbfb;
  padding: 9px 15px;
  font-size: 13px;
  line-height: 20px;
  color: #999999;
  border: 1px solid #ececec;
  border-radius: 5px;
  outline: none !important;
}

.check-data-pagination {
  top: -67px;
  display: inline-flex;
  left: calc(50% - 130px);
  z-index: 3;
}

.list-of-pills {
  .check-data-group {
    &__col1 {
      width: 74.2%;
      margin-bottom: 10px;
    }
    &__col2 {
      width: 24%;
      margin-bottom: 10px;
      padding-left: 50px;
      position: relative;
      margin-right: 0;
      .icon {
        position: absolute;
        left: 0;
        width: 45px;
        top: 50%;
        transform: translateY(-50%);
        margin-top: 10px;
        text-align: center;
        img {
          display: inline-block;
        }
      }
    }
    &__col3 {
      width: 58%;
      margin-right: 9px;
    }
    &__col5 {
      width: 40%;
    }
    &__desc {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      & + .group-size {
        margin-left: 40px;
      }
    }
    &__top {
      background: #fbfbfb;
      box-shadow: 0px 4px 25px rgba(0, 0, 0, 0.15);
      padding: 32px 0 37px 0;
      margin: -32px 0 0 0;
      position: relative;
      z-index: 1;
    }
    &__bottom {
      background: #f2f2f2;
    }
    .no_shape {
      .check-data-group {
        &__col1 {
          width: 38%;
        }
        &__col3 {
          width: 30%;
        }
        &__col5 {
          width: 30%;
        }
      }
    }
  }
}
.check-data-group {
  &--overflow {
    @media all and (min-height: 800px) {
      overflow: auto;
      max-height: calc(100vh - 666px);
      min-height: calc(100vh - 666px);
      padding: 25px;
      margin-left: -25px;
      margin-right: -25px;
    }
  }
  &__title {
    font-size: 16px;
    line-height: 17px;
    color: $color-medium-grey;
    margin: 59px 0 17px 0;

    &--main {
      color: $color-black-light;
      display: inline-flex;
      margin-top: 0;
    }
  }

  &__desc {
    width: 100%;
    display: flex;
  }
  &__item {
    background: $color-white;
    display: flex;
    border: 1px solid $color-grey;
    padding: 24px 40px;
    margin-bottom: 15px;
    &--suggestion {
      box-shadow: 0 0 20px rgb(153, 153, 153, 0.3);

      &,
      ::v-deep * {
        cursor: pointer;
      }
      &:hover,
      &.active {
        padding: 23px 39px;
        border: 2px solid $color-primary;
      }
    }
    .label-form {
      display: block;
      font-weight: bold;
      font-size: 13px;
      line-height: 14px;
      letter-spacing: 0.433333px;
      color: $color-medium-grey;
      margin: 0 0 7px 0;
    }
    &::v-deep .input {
      width: 100%;
      height: 40px;
      background: $color-faint-grey;
      padding: 10px 15px;
    }
  }

  .group-size {
    display: flex;
    flex-wrap: wrap;
    width: 620px;
    border-left: 1px solid #e4e4e4;
    margin: -24px 0 -24px 0;
    padding: 24px 0 24px 40px;
    &__item {
      width: 112px;
      margin-right: 8px;
      margin-bottom: 10px;
      &:nth-child(3n) {
        margin-right: 0;
      }
    }
  }
  &__col1 {
    width: 21.5%;
    margin-right: 7px;
  }
  &__col2 {
    width: 40%;
    margin-right: 9px;
  }
  &__col3 {
    width: 37%;
    margin-right: 7px;
  }
  &__col5 {
    width: 55%;
    margin-right: 0;
  }
  &__col4 {
    width: 15%;
    margin: 31px 0 0 25px;

    label {
      display: flex;
      align-items: center;
      @media all and (max-width: 1200px) {
        white-space: nowrap;
      }
      span {
        font-weight: bold;
        font-size: 13px;
        line-height: 14px;
        letter-spacing: 0.433333px;
        color: #333333;
        padding-left: 11px;
        cursor: pointer;
      }
    }
  }
}
.link-center {
  display: block;
  text-align: center;
  color: $color-primary;
  font-size: 13px;
  text-transform: uppercase;
  text-decoration: none;
  font-weight: bold;
  letter-spacing: 1.25px;
  margin: 20px 0;
}

.dot {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 16px;
  border-radius: 50%;
}

.list-item-select {
  position: absolute;
  width: 480px;
  left: 50%;
  margin-left: -240px;
  top: 45px;
  z-index: 10;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.15);
  padding: 23px 8px 23px 0;
  &__inner {
    max-height: 410px;
    overflow: auto;
    user-select: none;
  }
  &__item {
    padding: 9px 8px 8px 31px;
    font-size: 13px;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    &:hover {
      background: #f2f2f2;
    }
  }
}
#save-check-data-button {
  padding-left: 20px;
  padding-right: 20px;
}
</style>
